import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Search, Filter, X, RefreshCw } from "lucide-react";

interface Category {
  id: number;
  name: string;
}

interface Brand {
  id: number;
  name: string;
}

interface FilterControlsProps {
  nameFilter: string;
  setNameFilter: (value: string) => void;
  categoryFilter: string;
  setCategoryFilter: (value: string) => void;
  brandFilter: string;
  setBrandFilter: (value: string) => void;
  priceRange: [number, number];
  setPriceRange: (value: [number, number]) => void;
  maxPrice: number;
  categories: Category[];
  brands: Brand[];
  resetFilters: () => void;
}

const FilterControls = ({
  nameFilter,
  setNameFilter,
  categoryFilter,
  setCategoryFilter,
  brandFilter,
  setBrandFilter,
  priceRange,
  setPriceRange,
  maxPrice,
  categories,
  brands = [],
  resetFilters,
}: FilterControlsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasActiveFilters =
    nameFilter ||
    categoryFilter !== "all" ||
    brandFilter !== "all" ||
    priceRange[0] > 0 ||
    priceRange[1] < maxPrice;

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-700 shadow-elegant">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-neutral-100 dark:bg-neutral-800 rounded-xl">
            <Filter className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Filtros</h2>
          </div>
        </div>

        {/* Toggle for mobile */}
        <Button
          variant="ghost"
          size="sm"
          className="lg:hidden"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <X className="h-4 w-4" />
          ) : (
            <Filter className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Filter Content */}
      <div
        className={`p-6 space-y-6 ${isExpanded ? "block" : "hidden lg:block"}`}
      >
        {/* Search */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
            <Search className="h-4 w-4 text-neutral-500" />
            Buscar Producto
          </label>
          <div className="relative">
            <Input
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Escribe el nombre del producto..."
              className="h-12 border-neutral-300 dark:border-neutral-600 focus:border-neutral-500 dark:focus:border-neutral-400 rounded-xl"
            />
            {nameFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setNameFilter("")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Category Filter */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
            <div className="w-4 h-4 bg-neutral-400 dark:bg-neutral-600 rounded"></div>
            Categoría
          </label>
          <Select onValueChange={setCategoryFilter} value={categoryFilter}>
            <SelectTrigger className="h-12 border-neutral-300 dark:border-neutral-600 focus:border-neutral-500 dark:focus:border-neutral-400 rounded-xl">
              <SelectValue placeholder="Seleccionar categoría" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
              <SelectItem
                value="all"
                className="hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                🏷️ Todas
              </SelectItem>
              {categories.map((cat) => (
                <SelectItem
                  key={cat.id}
                  value={cat.id.toString()}
                  className="hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  📂 {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Brand Filter */}
        {brands.length > 0 && (
          <div className="space-y-3">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
              <div className="w-4 h-4 bg-neutral-400 dark:bg-neutral-600 rounded-full"></div>
              Marca
            </label>
            <Select onValueChange={setBrandFilter} value={brandFilter}>
              <SelectTrigger className="h-12 border-neutral-300 dark:border-neutral-600 focus:border-neutral-500 dark:focus:border-neutral-400 rounded-xl">
                <SelectValue placeholder="Seleccionar marca" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                <SelectItem
                  value="all"
                  className="hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  🏢 Todas
                </SelectItem>
                {brands.map((brand) => (
                  <SelectItem
                    key={brand.id}
                    value={brand.id.toString()}
                    className="hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  >
                    ⭐ {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Price Range */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-r from-brand-primary to-brand-secondary rounded"></div>
            Rango de Precio
          </label>

          <div className="px-3 py-4">
            <Slider
              value={priceRange}
              onValueChange={setPriceRange}
              max={maxPrice}
              step={10}
              className="w-full"
            />
          </div>

          <div className="flex justify-between items-center">
            <div className="bg-neutral-100 dark:bg-neutral-800 px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                S/{priceRange[0]}
              </span>
            </div>
            <div className="text-neutral-400 font-medium">—</div>
            <div className="bg-neutral-100 dark:bg-neutral-800 px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                S/{priceRange[1]}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <Button
            onClick={resetFilters}
            variant={hasActiveFilters ? "default" : "outline"}
            className={`w-full h-12 rounded-xl transition-all duration-300 ${
              hasActiveFilters
                ? "bg-brand-gradient text-black font-semibold hover:shadow-brand"
                : ""
            }`}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {hasActiveFilters ? "Limpiar Filtros" : "Sin Filtros Activos"}
          </Button>
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
              Filtros Activos:
            </h3>
            <div className="flex flex-wrap gap-2">
              {nameFilter && (
                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-lg text-xs font-medium">
                  📝 "{nameFilter}"
                </span>
              )}
               {categoryFilter !== "all" && (
                 <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-lg text-xs font-medium">
                   📂 Categoría
                 </span>
               )}
              {brandFilter !== "all" && (
                <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-3 py-1 rounded-lg text-xs font-medium">
                  ⭐ Marca
                </span>
              )}
              {(priceRange[0] > 0 || priceRange[1] < maxPrice) && (
                <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-3 py-1 rounded-lg text-xs font-medium">
                  💰 S/{priceRange[0]} - S/{priceRange[1]}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterControls;
